import { z } from 'zod';
import { HI_TYPES, type HiType } from '../constants';
import { RECORD_KINDS, heldRecordSchema } from './records';

/**
 * The practice protocol: what a member's device asks its practice's record
 * holder inside a practice_request envelope, and the answer that comes back
 * in a practice_response. Both travel encrypted, so the relay sees neither.
 *
 * sync: the practice's patients, as demographics changed after a cursor.
 * records: one patient's records the member may read, after a cursor.
 * get: one record.
 * put: a new version of a patient's demographics or of a document.
 * documents: documents of one type across patients, changed after a cursor,
 *   such as the prescriptions a pharmacy counter works from.
 * register: dispensing records across patients, changed after a cursor: the
 *   pharmacy's dispensing register.
 */
export const PRACTICE_OPS = ['sync', 'records', 'get', 'put', 'documents', 'register'] as const;
export type PracticeOp = (typeof PRACTICE_OPS)[number];

const cursor = z.number().int().min(0);
const base = { v: z.literal(1), requestId: z.uuid() };

export const recordWriteSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  kind: z.enum(RECORD_KINDS),
  hiType: z.enum(HI_TYPES).optional(),
  resource: z.record(z.string(), z.unknown()),
  /** For a dispensing record: the prescription it answers. */
  prescriptionId: z.uuid().optional(),
  /** The version this write replaces, or 0 for a new record. */
  baseVersion: z.number().int().min(0),
});
export type RecordWrite = z.infer<typeof recordWriteSchema>;

export const practiceRequestSchema = z.discriminatedUnion('op', [
  z.strictObject({ ...base, op: z.literal('sync'), since: cursor }),
  z.strictObject({ ...base, op: z.literal('records'), patientId: z.uuid(), after: cursor }),
  z.strictObject({ ...base, op: z.literal('get'), id: z.uuid() }),
  z.strictObject({ ...base, op: z.literal('put'), record: recordWriteSchema }),
  z.strictObject({ ...base, op: z.literal('documents'), hiType: z.enum(HI_TYPES), since: cursor }),
  z.strictObject({ ...base, op: z.literal('register'), since: cursor }),
]);
export type PracticeRequest = z.infer<typeof practiceRequestSchema>;

/** A request without its envelope fields, as the app builds it. */
export type PracticeQuery =
  | { op: 'sync'; since: number }
  | { op: 'records'; patientId: string; after: number }
  | { op: 'get'; id: string }
  | { op: 'put'; record: RecordWrite }
  | { op: 'documents'; hiType: HiType; since: number }
  | { op: 'register'; since: number };

const page = { records: z.array(heldRecordSchema), next: cursor, more: z.boolean() };

export const practiceResultSchema = z.discriminatedUnion('op', [
  z.strictObject({ op: z.literal('sync'), ...page }),
  z.strictObject({ op: z.literal('records'), patientId: z.uuid(), ...page }),
  z.strictObject({ op: z.literal('get'), record: heldRecordSchema }),
  z.strictObject({ op: z.literal('put'), record: heldRecordSchema }),
  z.strictObject({ op: z.literal('documents'), ...page }),
  z.strictObject({ op: z.literal('register'), ...page }),
]);
export type PracticeResult = z.infer<typeof practiceResultSchema>;

/**
 * invalid_request: the request or the record is malformed.
 * forbidden: the member's roles do not allow it.
 * not_found: no such patient or record.
 * conflict: someone saved a newer version first; the error carries it when the member may read it.
 * too_large: the record is over the size limit.
 */
export const PRACTICE_ERROR_CODES = ['invalid_request', 'forbidden', 'not_found', 'conflict', 'too_large'] as const;
export type PracticeErrorCode = (typeof PRACTICE_ERROR_CODES)[number];

export const practiceResponseSchema = z.discriminatedUnion('ok', [
  z.strictObject({ v: z.literal(1), requestId: z.uuid(), ok: z.literal(true), result: practiceResultSchema }),
  z.strictObject({
    v: z.literal(1),
    requestId: z.uuid(),
    ok: z.literal(false),
    error: z.strictObject({
      code: z.enum(PRACTICE_ERROR_CODES),
      message: z.string().max(300),
      record: heldRecordSchema.optional(),
    }),
  }),
]);
export type PracticeResponse = z.infer<typeof practiceResponseSchema>;
